import React, { useEffect, useState } from "react";
import Header from "./Header/Header";
import Menu from "./Menu/Menu";
import useSize from "../../../Utils/useSize/useSize";
import Backend from "../../../Backend/Backend";
import toast from "react-hot-toast";
import Loading from "../../../Utils/Loading/Loading";
import NotFound from "../../../Utils/NotFound/NotFound";
import SymbolCard from "./SymbolCard/SymbolCard";

export default function Watchlist() {
  const windowSize = useSize();
  const [backend, setBackend] = useState(null);

  const [loading, setLoading] = useState(false);
  const [type, setType] = useState(1);

  const [watchlist, setWatchlist] = useState([]);
  const [allSymbols, setAllSymbols] = useState([]);

  const [nseFuture, setNseFuture] = useState({ active: 0, symbol: [] });
  const [nseEquity, setNseEquity] = useState({ active: 0, symbol: [] });
  const [mcxFuture, setMcxFuture] = useState({ active: 0, symbol: [] });
  const [mcxOptions, setMcxOptions] = useState({ active: 0, symbol: [] });
  const [nseOptions, setNseOptions] = useState({ active: 0, symbol: [] });
  const [comex, setComex] = useState({ active: 0, symbol: [] });
  const [crypto, setCrypto] = useState({ active: 0, symbol: [] });
  const [forex, setForex] = useState({ active: 0, symbol: [] });

  const [usStock, setUsStock] = useState({ active: 0, symbol: [] });
  const [sgx, setSgx] = useState({ active: 0, symbol: [] });
  const [index, setIndex] = useState({ active: 0, symbol: [] });

  const [dgcx, setDgcx] = useState({ active: 0, symbol: [] });
  const [ncdex, setNcdex] = useState({ active: 0, symbol: [] });

  // ✅ Initialize backend instance once
  useEffect(() => {
    (async () => {
      const instance = await new Backend().init();
      setBackend(instance);
    })();
  }, []);

  // ✅ Load symbols when backend ready or type changes
  useEffect(() => {
    if (backend) loadSymbol();
  }, [backend, type]);

  const loadSymbol = async () => {
    try {
      setLoading(true);
      const data = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        user_id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        watchlist_id: type,
      };

      const r = await backend.watchlist(data);
      setLoading(false);

      if (r.error === "False") {
        // Set extra info for F&O etc.
        setNseFuture({ active: r.nse_future, symbol: r.nse_future_symbol });
        setNseEquity({ active: r.nse_equity, symbol: r.nse_equity_symbol });
        setMcxFuture({ active: r.mcx_future, symbol: r.mcx_future_symbol });
        setMcxOptions({ active: r.mcx_options, symbol: r.mcx_options_symbol });
        setNseOptions({ active: r.nse_options, symbol: r.nse_options_symbol });

        setComex({ active: r.comex, symbol: r.comex_symbol });
        setCrypto({ active: r.crypto, symbol: r.crypto_symbol });
        setForex({ active: r.forex, symbol: r.forex_symbol });

        setUsStock({ active: r.us_stock, symbol: r.us_stock_symbol });
        setSgx({ active: r.sgx, symbol: r.sgx_symbol });
        setIndex({ active: r.index, symbol: r.index_symbol });

        setDgcx({ active: r.dgcx, symbol: r.dgcx_symbol });
        setNcdex({ active: r.ncdex, symbol: r.ncdex_symbol });

        // Determine which list to show
        let selectedList = [];
        switch (type) {
          case 1:
            selectedList = r.nse_future_symbol || [];
            break;
          case 2:
            selectedList = r.nse_equity_symbol || [];
            break;
          case 3:
            selectedList = r.mcx_future_symbol || [];
            break;
          case 4:
            selectedList = r.mcx_options_symbol || [];
            break;
          case 5:
            selectedList = r.nse_options_symbol || [];
            break;
          case 6:
            selectedList = r.comex_symbol || [];
            break;
          case 7:
            selectedList = r.crypto_symbol || [];
            break;
          case 8:
            selectedList = r.forex_symbol || [];
            break;
          case 9:
            selectedList = r.us_stock_symbol || [];
            break;
          case 10:
            selectedList = r.sgx_symbol || [];
            break;
          case 11:
            selectedList = r.index_symbol || [];
            break;
          case 12:
            selectedList = r.dgcx_symbol || [];
            break;
          case 13:
            selectedList = r.ncdex_symbol || [];
            break;
          default:
            selectedList = [];
        }

        setWatchlist(selectedList);
        setAllSymbols(selectedList);
      } else {
        if (r.logout === 1) window.LogOut?.();
        else toast.error(r.message || "Failed to load watchlist");
      }
    } catch (err) {
      console.error("Watchlist load error:", err);
      setLoading(false);
      toast.error("Something went wrong while loading symbols");
    }
  };

  const handleSearch = (query) => {
    if (!query || query.length < 3) {
      setWatchlist(allSymbols);
      return;
    }

    const filtered = allSymbols.filter((x) =>
      x.symbol_display.toLowerCase().includes(query.toLowerCase())
    );
    setWatchlist(filtered);
  };

  const handleTabChange = (newType) => {
    if (type !== newType) setType(newType);
  };

  return (
    <div>
      <Header
        updateTab={(type) => handleTabChange(type)}
        reload={loadSymbol}
        search={handleSearch}
        type={type}
        nse_future_active={nseFuture.active}
        nse_equity_active={nseEquity.active}
        mcx_future_active={mcxFuture.active}
        mcx_options_active={mcxOptions.active}
        nse_options_active={nseOptions.active}
        comex={comex.active}
        crypto={crypto.active}
        forex={forex.active}
        usstock={usStock.active}
        sgx={sgx.active}
        index={index.active}
        dgcx={dgcx.active}
        ncdex={ncdex.active}
      />
      <Menu />

      <div
        style={{
          width: windowSize[0],
          height: windowSize[1] - 175,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <Loading height={windowSize[1] - 175} />
          </div>
        ) : watchlist.length > 0 ? (
          <div
            style={{
              overflowY: "auto",
              height: windowSize[1] - 175,
            }}
          >
            {watchlist.map((item, index) => (
              <SymbolCard
                key={item.id || index}
                item={item}
                index={index + 1}
                type={type}
                reload={loadSymbol}
              />
            ))}
          </div>
        ) : (
          <NotFound
            height={windowSize[1] - 160}
            title="Nothing here"
            subtext="Use the search bar to add instruments."
          />
        )}
      </div>
    </div>
  );
}
